import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, Stack, TextField, MenuItem } from "@mui/material";
import {MaterialReactTable} from "material-react-table";
import { io } from "socket.io-client";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";

const SOCKET_URL = "http://51.84.12.132:4001"; // תשנה בפרוד
const API_BASE = ""; // same-origin

function levelChip(level) {
    const v = String(level || "info");
    const color =
        v === "error" ? "error" :
            v === "warn"  ? "warning" :
                v === "debug" ? "default" :
                    "info";
    return <Chip size="small" label={v} color={color} variant="outlined" />;
}

function humanizeLog(row) {
    const channel = row.channel;
    const source = row.source;
    const msg = String(row.msg || "");
    const data = row.data || {};
    const ctx = row.ctx || {};

    // HTTP logs (מהלוגר שלך)
    if (channel === "app" && source === "http") {
        const m = msg.match(/^(\w+)\s+([^\s]+)\s+->\s+(\d+)\s+\((\d+)ms\)/);
        const method = m?.[1];
        const path = m?.[2];
        const status = m?.[3] ? Number(m[3]) : (data.statusCode ?? undefined);
        const durationMs = m?.[4] ? Number(m[4]) : (data.durationMs ?? undefined);

        const ok = status && status < 400;
        return {
            title: ok ? "קריאת API הצליחה" : "קריאת API נכשלה",
            fields: [
                { label: "Endpoint", value: method && path ? `${method} ${path}` : msg },
                { label: "סטטוס", value: status ?? "—" },
                { label: "זמן תגובה", value: durationMs != null ? `${durationMs}ms` : "—" },
                { label: "IP", value: ctx.ip ?? "—" },
                { label: "Request ID", value: ctx.reqId ?? "—" },
            ],
            rawHint: ok ? null : (data.errorMessage || data.errorBody || null),
        };
    }

    // CloudWatch syslog/kernel - אירועי תשתית/שירות
    if (channel === "infra" && source === "cloudwatch") {
        const isService = msg.includes("devBlueParkingInvite.service");
        const isOOM = msg.toLowerCase().includes("out of memory") || msg.toLowerCase().includes("oom");

        let title = "אירוע תשתית";
        if (isOOM) title = "OOM / חוסר זיכרון";
        else if (isService && msg.includes("Started")) title = "השירות עלה (Started)";
        else if (isService && (msg.includes("Stopped") || msg.includes("Stopping"))) title = "השירות נעצר (Stopped)";
        else if (isService && (msg.includes("Failed") || msg.includes("Main process exited"))) title = "השירות נפל (Failed)";
        else if (msg.includes("Kernel panic")) title = "Kernel panic";

        return {
            title,
            fields: [
                { label: "שירות", value: isService ? "devBlueParkingInvite.service" : "—" },
                { label: "מקור", value: (ctx.logGroup || "—") },
                { label: "הודעה", value: msg.split("\n").slice(0, 5).join("\n") },
            ],
            rawHint: null,
        };
    }

    // fallback כללי
    return {
        title: "לוג מערכת",
        fields: [
            { label: "הודעה", value: msg },
            { label: "ערוץ", value: channel },
            { label: "מקור", value: source },
        ],
        rawHint: null,
    };
}


export default function LogsViewer() {
    const socketRef = useRef(null);

    const [rows, setRows] = useState([]);
    const [nextBeforeId, setNextBeforeId] = useState(null);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);

    // filters
    const [channel, setChannel] = useState("");
    const [level, setLevel] = useState("");
    const [source, setSource] = useState("");
    const [q, setQ] = useState("");

    const columns = useMemo(() => [
        {
            accessorKey: "ts",
            header: "זמן",
            size: 170,
            Cell: ({ cell }) => {
                const v = cell.getValue();
                if (!v) return "—";

                let date;

                if (typeof v === "number") {
                    // timestamp (שניות / מילישניות)
                    date = new Date(v < 1e12 ? v * 1000 : v);
                } else if (typeof v === "string") {
                    // אם אין Z – נניח שזה UTC
                    date = v.endsWith("Z") ? new Date(v) : new Date(v + "Z");
                } else {
                    date = new Date(v);
                }

                const israelTime = new Intl.DateTimeFormat("he-IL", {
                    timeZone: "Asia/Jerusalem",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                }).format(date);

                return (
                    <span style={{ fontFamily: "monospace", direction: "ltr" }}>
      {israelTime}
    </span>
                );
            }

        },
        { accessorKey: "channel", header: "ערוץ", size: 80 },
        {
            accessorKey: "level",
            header: "רמה",
            size: 80,
            Cell: ({ cell }) => levelChip(cell.getValue()),
        },
        { accessorKey: "source", header: "מקור", size: 120 },
        {
            accessorKey: "eventTitle",
            header: "סוג",
            size: 160,
            Cell: ({ row }) => {
                const h = humanizeLog(row.original);
                return <span>{h.title}</span>;
            },
        },
        {
            accessorKey: "msg",
            header: "הודעה",
            size: 600,
            Cell: ({ cell }) => (
                <span style={{ whiteSpace: "pre-wrap" }}>{String(cell.getValue() || "")}</span>
            ),
        },
    ], []);

    async function loadInitial() {
        const params = new URLSearchParams();
        params.set("limit", "200");
        if (q) params.set("q", q);

        if (channel) params.set("channel", channel);
        if (level) params.set("level", level);
        if (source) params.set("source", source);


        const resp = await fetch(`/logs-api/api/logs?${params.toString()}`);
        const data = await resp.json();
        if (!data?.ok) return;

        setRows(data.items || []);
        setNextBeforeId(data.nextBeforeId || null);
    }

    async function loadOlder() {
        if (!nextBeforeId || isLoadingOlder) return;
        setIsLoadingOlder(true);
        try {
            const params = new URLSearchParams();
            params.set("limit", "200");
            params.set("beforeId", String(nextBeforeId));
            if (q) params.set("q", q);

            if (channel) params.set("channel", channel);
            if (level) params.set("level", level);
            if (source) params.set("source", source);

            const resp = await fetch(`/logs-api/api/logs?${params.toString()}`);
            const data = await resp.json();
            if (!data?.ok) return;

            const older = data.items || [];
            setRows((prev) => [...prev, ...older]);
            setNextBeforeId(data.nextBeforeId || null);
        } finally {
            setIsLoadingOlder(false);
        }
    }

    function connectSocketAndSubscribe() {
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        const s = io("/", {
            path: "/logs-socket",
            transports: ["websocket"],
        });
        socketRef.current = s;

        s.on("connect", () => {
            // subscribe
            const filters = {
                channels: channel ? [channel] : ["app", "service", "infra"],
                levels: level ? [level] : ["debug", "info", "warn", "error"],
                sources: source ? [source] : undefined,
                // caseId: caseId || undefined,
                q: q || undefined,
            };
            s.emit("logs:subscribe", { filters });
        });

        s.on("logs:append", ({ items } = {}) => {
            if (!Array.isArray(items) || !items.length) return;

            // newest first on top; also prevent duplicates by id if you later add id to socket payload
            setRows((prev) => [...items, ...prev].slice(0, 2000)); // keep a cap
        });
    }

    // initial load + resubscribe when filters change
    useEffect(() => {
        loadInitial();
        connectSocketAndSubscribe();
        return () => socketRef.current?.disconnect();
    }, [channel, level, source, q]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
        <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                    select
                    size="small"
                    label="ערוץ"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    sx={{
                        minWidth: 140,
                        "& .MuiInputBase-root": { height: 38 },          // הגובה של השדה
                        "& .MuiInputBase-input": { paddingY: 0 },        // שלא יגדיל בגלל padding
                    }}                >
                    <MenuItem value="app">app</MenuItem>
                    <MenuItem value="service">service</MenuItem>
                    <MenuItem value="infra">infra</MenuItem>
                    <MenuItem value="">all</MenuItem>
                </TextField>

                <TextField
                    select
                    size="small"
                    label="רמה"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    sx={{
                        minWidth: 140,
                        "& .MuiInputBase-root": { height: 38 },          // הגובה של השדה
                        "& .MuiInputBase-input": { paddingY: 0 },        // שלא יגדיל בגלל padding
                    }}                >
                    <MenuItem value="">all</MenuItem>
                    <MenuItem value="debug">debug</MenuItem>
                    <MenuItem value="info">info</MenuItem>
                    <MenuItem value="warn">warn</MenuItem>
                    <MenuItem value="error">error</MenuItem>
                </TextField>

                <TextField
                    size="small"
                    label="source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    sx={{ minWidth: 160 }}
                />


                <TextField
                    size="small"
                    label="חיפוש טקסט"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    sx={{ minWidth: 220 }}
                />

                <Button variant="outlined" onClick={loadInitial}>
                    רענן
                </Button>

                <Button
                    variant="contained"
                    disabled={!nextBeforeId || isLoadingOlder}
                    onClick={loadOlder}
                >
                    {isLoadingOlder ? "טוען..." : "טען עוד אחורה"}
                </Button>
            </Stack>

            <MaterialReactTable
                columns={columns}
                data={rows}
                getRowId={(row) => String(row.id ?? `${row.ts}-${row.msg}`)}
                enableStickyHeader
                enableColumnResizing
                enableDensityToggle={false}
                muiTopToolbarProps={{
                    sx: {
                        color: "#fff",
                        "& .MuiTypography-root": { color: "#fff" },
                        "& .MuiInputBase-root": { color: "#fff" },
                        "& .MuiSvgIcon-root": { color: "#fff" },
                        "& .MuiButtonBase-root": { color: "#fff" },
                    },
                }}

                muiBottomToolbarProps={{
                    sx: {
                        color: "#fff",
                        "& .MuiTypography-root": { color: "#fff" },
                        "& .MuiSvgIcon-root": { color: "#fff" },
                        "& .MuiButtonBase-root": { color: "#fff" },
                    },
                }}

                muiTablePaginationProps={{
                    sx: {
                        color: "#fff",
                        "& .MuiSelect-icon": { color: "#fff" },
                        "& .MuiSvgIcon-root": { color: "#fff" },
                        "& .MuiTablePagination-actions button": { color: "#fff" },
                    },
                }}
                muiTableBodyRowProps={{
                    sx: {
                        "& .MuiIconButton-root": {
                            color: "#fff",
                        },
                        "& .MuiSvgIcon-root": {
                            color: "#fff",
                        },
                    },
                }}
                renderDetailPanel={({ row }) => {
                    const h = humanizeLog(row.original);
                    return (
                        <Box sx={{ p: 2, backgroundColor: "rgba(2, 6, 23, 0.6)", color: "#fff" }}>
                            <Box sx={{ fontWeight: 700, mb: 1, fontSize: 16 }}>{h.title}</Box>

                            <Stack spacing={0.75} sx={{ mb: 1 }}>
                                {h.fields.map((f, idx) => (
                                    <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                                        <Box sx={{ opacity: 0.75, minWidth: 110 }}>{f.label}:</Box>
                                        <Box sx={{ whiteSpace: "pre-wrap", fontFamily: f.label === "הודעה" ? "monospace" : "inherit" }}>
                                            {String(f.value ?? "—")}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>

                            {/* Raw technical details for devs */}
                            <Box sx={{ mt: 1, opacity: 0.9 }}>
                                <Box sx={{ fontWeight: 600, mb: 0.5 }}>פרטים טכניים</Box>
                                <pre style={{ margin: 0, color: "#fff", fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
{`ctx:\n${JSON.stringify(row.original.ctx || {}, null, 2)}\n\ndata:\n${JSON.stringify(row.original.data || {}, null, 2)}`}
        </pre>
                            </Box>
                        </Box>
                    );
                }}
                // ✅ כותרות
                muiTableHeadCellProps={{
                    sx: {
                        color: "#fff",
                        "& .MuiTableSortLabel-root": { color: "#fff" },
                        "& .MuiTableSortLabel-icon": { color: "#fff !important" },
                        "& .MuiSvgIcon-root": { color: "#fff" },
                    },
                }}

                // ✅ תאים בגוף הטבלה
                muiTableBodyCellProps={{
                    sx: { color: "#fff" },
                }}

                // ✅ רקע כללי כדי שיהיה קריא
                muiTablePaperProps={{
                    sx: {
                        backgroundColor: "rgba(15, 23, 42, 0.7)", // דומה לסגנון שלך
                    },
                }}
                muiTableHeadProps={{
                    sx: {
                        display: "table-header-group",
                        // backgroundColor: "rgb(0,0,0)",
                        color: "white",
                        "&.Mui-TableHeadCell-Content-Wrapper": { color: "white" },
                    },
                }}
                enableFullScreenToggle
                muiTableContainerProps={{ sx: { maxHeight: "70vh" } }}
                initialState={{ density: "compact" }}
            />
        </Box>
        </DashboardLayout>
    );
}
